import { useMemo, useState } from "react";
import { CalendarTable } from "./components/CalendarTable";
import { useCalendarCategoryHistory, useCalendarV2Events } from "./hooks";
import {
  formatDayLabel,
  shiftDateString,
  todayLocalDateString,
} from "./utils/date";
import "./calendar-v2.css";

function formatUpdatedAt(epochMs: number): string {
  if (!epochMs) return "";
  return new Date(epochMs).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Calendar V2 — the Newsquawk economic calendar rendered from the content-hub
 * `newsquawk-calendar-event` feed. Shows one UTC day at a time, selected via a
 * date field (defaults to today). Sits alongside the legacy /calendar page,
 * which keeps its own API integration.
 */
function CalendarV2Page() {
  const [date, setDate] = useState<string>(() => todayLocalDateString());
  const [showLowHigh, setShowLowHigh] = useState(false);
  const [showChart, setShowChart] = useState(true);

  const {
    events,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    dataUpdatedAt,
    changedEventUuids,
    clearChangedStatus,
    isDelayed,
  } = useCalendarV2Events(date);

  const today = todayLocalDateString();
  const isToday = date === today;

  // Category ids of the day's data events → fetch their history for the
  // sparklines (only while the Trend toggle is on).
  const categoryIds = useMemo(() => {
    const ids: number[] = [];
    for (const e of events) {
      const id = (e as { category_id?: number | null }).category_id;
      if (typeof id === "number") ids.push(id);
    }
    return ids;
  }, [events]);
  const categoryHistory = useCalendarCategoryHistory(categoryIds, showChart);

  return (
    <div className="calendar-v2 h-screen flex flex-col p-2 gap-2 bg-white dark:bg-gray-950">
      <header className="flex flex-wrap items-center justify-between gap-3 px-1">
        <div className="flex items-center gap-2">
          <h1 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Calendar
          </h1>
          {isDelayed && (
            <span className="rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-300">
              24h delayed
            </span>
          )}
        </div>

        {/* Day selector */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            aria-label="Previous day"
            onClick={() => setDate((d) => shiftDateString(d, -1))}
            className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            ‹
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value || today)}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
          />
          <button
            type="button"
            aria-label="Next day"
            onClick={() => setDate((d) => shiftDateString(d, 1))}
            className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            ›
          </button>
          <button
            type="button"
            onClick={() => setDate(today)}
            disabled={isToday}
            className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-default"
          >
            Today
          </button>
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showLowHigh}
              onChange={(e) => setShowLowHigh(e.target.checked)}
              className="accent-blue-600"
            />
            Forecast range
          </label>
          <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showChart}
              onChange={(e) => setShowChart(e.target.checked)}
              className="accent-blue-600"
            />
            Trend
          </label>
          <button
            type="button"
            onClick={() => refetch()}
            className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
            title={dataUpdatedAt ? `Updated ${formatUpdatedAt(dataUpdatedAt)}` : undefined}
          >
            <span
              className={`h-2 w-2 rounded-full ${
                isFetching ? "bg-amber-500 animate-pulse" : "bg-green-500"
              }`}
            />
            {isFetching
              ? "Refreshing…"
              : dataUpdatedAt
              ? `Updated ${formatUpdatedAt(dataUpdatedAt)}`
              : "Refresh"}
          </button>
        </div>
      </header>

      <div className="px-1 text-sm font-medium text-gray-500 dark:text-gray-400">
        {formatDayLabel(date)}
        {isToday && " · Today"}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800">
        {isError ? (
          <p className="p-3 text-sm text-red-600 dark:text-red-400">
            Failed to load calendar:{" "}
            {error instanceof Error ? error.message : "unknown error"}
          </p>
        ) : (
          <>
            <CalendarTable
              events={events}
              loading={isLoading}
              showLowHigh={showLowHigh}
              changedEventUuids={changedEventUuids}
              clearChangedStatus={clearChangedStatus}
              chart={showChart ? "mini" : undefined}
              categoryHistory={categoryHistory}
            />
            {!isLoading && events.length === 0 && (
              <p className="p-3 text-sm text-gray-500 dark:text-gray-400">
                No calendar events on this day.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default CalendarV2Page;
